/**
 * Define your feature keys here. Please keep them in alphabetical order.
 */
export enum GrowthBookFeatureKeys {
  'artist-services' = 'artist-services',
  'charts-rollout' = 'charts-rollout',
  'curator-onboarding' = 'curator-onboarding',
  'curator-mapping' = 'curator-mapping',
  'hide-discount-banner' = 'hide-discount-banner',
  'hype-rollout-AB' = 'hype-rollout-AB',
  'hype-rollout' = 'hype-rollout',
  'pack-payment-page-multiplier' = 'pack-payment-page-multiplier',
  'ratings-social-proof' = 'ratings-social-proof',
  'shared-music-step' = 'shared-music-step',
  'soundcloud-auth-rollout' = 'soundcloud-auth-rollout',
  'spotify-artist-link' = 'spotify-artist-link',
  'subscription-groover-plus' = 'subscription-groover-plus',
  'track-conversion' = 'track-conversion',
  'tracking-landing-page' = 'tracking-landing-page',
  'transfer-bonus' = 'transfer-bonus',
  'user-generated-content' = 'user-generated-content',
  'onboard-and-send' = 'onboard-and-send',
}

export type GrowthBookFeatureName = `${GrowthBookFeatureKeys}`

type ControlGroup = 'control'

/**
 * Define your feature groups below. Please keep them in alphabetical order.
 */
export type ArtistServicesFeatureGroups =
  | ControlGroup
  | 'artist-bio'
  | 'press-release'
  | 'ads-campaign'
  | 'groover-club'
  | 'general-consultation-call'
  | 'release-plan-call'
  | 'mastering'
  | 'base-for-music'

export type ChartsRolloutFeatureGroups = ControlGroup | 'enabled'

export type CuratorOnboardingFeatureGroups = ControlGroup | 'new-flow'

export type CuratorMappingFeatureGroups = ControlGroup | 'v2'

export type CuratorReferralPageTextFeatureGroups =
  | ControlGroup
  | 'receive-more-music'

export type DeclutteringGrooverFeatureGroups =
  | ControlGroup
  | 'onboarding-campaign-updates'
  | 'all-changes-plus-opportunities'

export type FilterFlowFeatureGroups = ControlGroup | 'no-flow' | 'types-only'

export type HideDiscountBannerFeatureGroups = ControlGroup | 'banner-hidden'

/**
 * HypeRolloutFeatureGroups is a rollout flag that runs alongside the 'hype-rollout-AB' AB test.
 */
export type HypeRolloutFeatureGroups = {
  is_enabled: boolean
  hype_price?: number
  num_opportunities_received?: number
}

/**
 * HypeRolloutABFeatureGroups is an AB test that runs alongside the 'hype-rollout' rollout flag.
 */
export type HypeRolloutABFeatureGroups = ControlGroup | 'hype-exposed'

export type LandingPageFeatureGroups =
  | ControlGroup
  | 'break-out'
  | 'get-heard-everywhere'
  | 'scrolling'

export type PackPaymentPageMultiplierFeatureGroups =
  | ControlGroup
  | 'increased-multiplier'
  | 'bigger-spenders'

export type RatingsSocialProofFeatureGroups =
  | ControlGroup
  | 'satisfaction-rate'
  | 'stars'

export type ReleaseNewLanguagesFeatureGroups = ControlGroup | 'enabled'

export type SharedMusicStepFeatureGroups =
  | ControlGroup
  | 'no-shared-music'
  | 'shared-music-step'

export type SpotifyLinkSelectorFeatureGroups =
  | ControlGroup
  | 'select-from-dropdown'

export type SubscriptionGrooverPlusFeatureGroups = ControlGroup | 'enabled'

export type TrackConversionFeatureGroups =
  | ControlGroup
  | 'converting-links-enabled'

export type SoundCloudAuthRolloutFeatureGroups =
  | ControlGroup
  | 'soundcloud-auth-enabled'

export type TrackingLandingPageFeatureGroups =
  | ControlGroup
  | 'landing-page-viewed-tracked'

export type TransferBonusFeatureGroups =
  // ControlGroup needed for several functions from GrowthBookHelper.ts to work
  | ControlGroup
  | {
      is_enabled: boolean
      min_bonus_eligible_amount: number
      bonus: number
    }

export type UserGeneratedContentFeatureGroups =
  | ControlGroup
  | 'user-generated-content-enabled'

export type OboardAndSendFeatureGroups = ControlGroup | 'v2'

/**
 * Utility type for GrowthBook features
 */
type GrowthBookFeatureDefinition<
  C extends GrowthBookFeatureName,
  V extends string | Record<string, any>,
> = {
  [K in C]: V
}

/**
 * Define your GB features below. Please keep them in alphabetical order.
 */
export type GrowthBookFeatureLib = GrowthBookFeatureDefinition<
  'artist-services',
  ArtistServicesFeatureGroups
> &
  GrowthBookFeatureDefinition<'charts-rollout', ChartsRolloutFeatureGroups> &
  GrowthBookFeatureDefinition<
    'curator-onboarding',
    CuratorOnboardingFeatureGroups
  > &
  GrowthBookFeatureDefinition<'curator-mapping', CuratorMappingFeatureGroups> &
  GrowthBookFeatureDefinition<
    'hide-discount-banner',
    HideDiscountBannerFeatureGroups
  > &
  GrowthBookFeatureDefinition<'hype-rollout', HypeRolloutFeatureGroups> &
  GrowthBookFeatureDefinition<'hype-rollout-AB', HypeRolloutABFeatureGroups> &
  GrowthBookFeatureDefinition<
    'pack-payment-page-multiplier',
    PackPaymentPageMultiplierFeatureGroups
  > &
  GrowthBookFeatureDefinition<
    'ratings-social-proof',
    RatingsSocialProofFeatureGroups
  > &
  GrowthBookFeatureDefinition<
    'shared-music-step',
    SharedMusicStepFeatureGroups
  > &
  GrowthBookFeatureDefinition<
    'soundcloud-auth-rollout',
    SoundCloudAuthRolloutFeatureGroups
  > &
  GrowthBookFeatureDefinition<
    'spotify-artist-link',
    SpotifyLinkSelectorFeatureGroups
  > &
  GrowthBookFeatureDefinition<
    'subscription-groover-plus',
    SubscriptionGrooverPlusFeatureGroups
  > &
  GrowthBookFeatureDefinition<
    'track-conversion',
    TrackConversionFeatureGroups
  > &
  GrowthBookFeatureDefinition<
    'tracking-landing-page',
    TrackingLandingPageFeatureGroups
  > &
  GrowthBookFeatureDefinition<'transfer-bonus', TransferBonusFeatureGroups> &
  GrowthBookFeatureDefinition<
    'user-generated-content',
    UserGeneratedContentFeatureGroups
  > &
  GrowthBookFeatureDefinition<'onboard-and-send', OboardAndSendFeatureGroups>

export type GrowthBookApiResponse = {
  [K in GrowthBookFeatureName]: {
    defaultValue: 'control'
    rules: [
      {
        variations: GrowthBookFeatureLib[K][]
        coverage: number
        weights: number[]
        key: K
        hashAttribute: 'id' | any
      },
    ]
  }
}
